import React from 'react';

import classNames from 'classnames';

import AngleRightIcon from '@ast/magma/components/icon/icons/AngleRight';

import ActionUtils from '@ast/magma/inner/utils/action';

import { isTruthy } from '@app/common/utils';

import styles from './ActionGroupItem.pcss';

/**
 * Action group item root element type.
 *
 * The list item type is not allowed because the action group item should have a `button` role.
 */
export type ActionGroupItemElement = HTMLDivElement;

/**
 * Action group item props.
 */
export interface ActionGroupItemProps extends React.HTMLAttributes<ActionGroupItemElement> {
  /**
   * Callback to fire when user selects item by clicking or pressing a key.
   */
  readonly onSelect?: () => void;

  /**
   * External className for group-item icon customization.
   */
  readonly iconClassName?: string;
}

/**
 * Action group item.
 *
 * May contain any other elements.
 * May be disabled by using the `aria-disabled` attribute.
 *
 * Primarily used as a child of the {@link ActionGroup} component:
 * ```tsx
 * <ActionGroup>
 *   <ActionGroupItem onSelect={() => ('Action 1')}>
 *     Action 1
 *   </ActionGroupItem>
 *   <ActionGroupItem onSelect={() => ('Action 2')}>
 *     Action 2
 *   </ActionGroupItem>
 *   <ActionGroupItem onSelect={() => ('Action 3')} aria-disabled={true}>
 *     Action 3
 *   </ActionGroupItem>
 * <ActionGroup>
 * ```
 */
export const ActionGroupItem = React.forwardRef((
  props: ActionGroupItemProps,
  ref: React.Ref<ActionGroupItemElement>,
) => {
  const disabledAttribute = 'aria-disabled';
  const tabIndexDefault = 0;

  const handleOnSelect = () => {
    // Prevent select callback from firing if disabled
    if (!isTruthy(props[disabledAttribute]) && props.onSelect) {
      props.onSelect();
    }
  };

  // Destruct props to do proper mapping
  const {
    onSelect,
    onClick,
    onKeyUp,
    className,
    iconClassName,
    children,
    tabIndex,
    ...restProps
  } = props;

  return (
    <div
      className={classNames(styles.item, className)}
      role="button"
      ref={ref}
      tabIndex={tabIndex || tabIndexDefault}
      {...ActionUtils.handleSelect(handleOnSelect, props.onClick, props.onKeyUp)}
      {...restProps}
    >
      <div className={styles.content}>
        {props.children}
      </div>
      <div className={classNames(styles.icon, iconClassName)}>
        <AngleRightIcon />
      </div>
    </div>
  );
});
